/**
 * Customer prices (#3034): a fixed net price or a percent discount for one customer (a company OR a person) on
 * one part or one category, optionally within a date window. Where several rules (customer price, campaign) apply, the
 * server picks the cheapest and never stacks them; existing orders are never repriced by a rule.
 * Everyone with `inventory.read` may browse; creating, editing and deleting need `inventory.pricing.manage`.
 */

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Chip,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  MenuItem,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { ResponsiveDialog } from '@/components/common/ResponsiveDialog';
import { ConfirmDialog } from '@/components/common/ConfirmDialog';
import { useNotify } from '@/stores/notificationStore';
import {
  flattenPages,
  useCreateCustomerPrice,
  useCustomerPrices,
  useDeleteCustomerPrice,
  useKeyedSubmit,
  usePricingPermissions,
  useUpdateCustomerPrice,
  type CreateCustomerPriceBody,
  type CustomerPriceFilters,
  type CustomerPriceRule,
  type CustomerPriceSaved,
  type UpdateCustomerPriceBody,
} from '@/hooks/usePartPricing';
import { cents4ToEurosInput, checkWindow, formatDate, normalizeCents4, percentToInput } from './decimal';
import { JournalStatusNotice, PricingErrorAlert, UncertainOutcomeAlert } from './feedback';
import { OwnedBoundary } from './OwnedBoundary';
import { useJournalView } from './pricingJournal';
import { MANAGE_PERMISSIONS, isSessionCancel, usePricingAccess } from './pricingSession';
import { describePricingError } from './pricingErrors';
import { CategoryName, PartName, PartyName, PricingPartyPicker, type PickedParty } from './pickers';
import {
  PricingWarnings,
  RuleValueFields,
  TargetFields,
  WindowFields,
  emptyTarget,
  parseRuleValue,
  targetBody,
  useRuleValueText,
  type RuleValueDraft,
  type TargetDraft,
} from './ruleForm';

const NOTE_MAX = 500;

function valueDraftOf(rule: CustomerPriceRule): RuleValueDraft {
  return {
    priceType: rule.priceType,
    valueText: rule.priceType === 'fixed_price' ? cents4ToEurosInput(rule.value) : percentToInput(rule.value),
  };
}

interface DialogProps {
  /** The rule being edited, or null to create one. */
  rule: CustomerPriceRule | null;
  onClose: () => void;
}

function CustomerPriceDialog({ rule, onClose }: DialogProps) {
  const { t } = useTranslation('partPricing');
  const { t: tc } = useTranslation('common');
  const notify = useNotify();
  const create = useCreateCustomerPrice();
  const update = useUpdateCustomerPrice();
  const createSubmit = useKeyedSubmit<CreateCustomerPriceBody, CustomerPriceSaved>(create.mutateAsync, {
    journal: { kind: 'customer_price_create', target: null },
    permissions: MANAGE_PERMISSIONS,
  });

  const [party, setParty] = useState<PickedParty | null>(null);
  const [target, setTarget] = useState<TargetDraft>(emptyTarget);
  const [valueDraft, setValueDraft] = useState<RuleValueDraft>(
    rule ? valueDraftOf(rule) : { priceType: 'fixed_price', valueText: '' },
  );
  const [period, setPeriod] = useState({ from: rule?.validFrom ?? '', to: rule?.validTo ?? '' });
  const [isActive, setIsActive] = useState(rule?.isActive ?? true);
  const [note, setNote] = useState(rule?.note ?? '');
  const [showErrors, setShowErrors] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const [savedWarnings, setSavedWarnings] = useState<CustomerPriceSaved['warnings'] | null>(null);

  const held = createSubmit.held !== null;
  const damaged = createSubmit.journalStatus === 'corrupt';
  const busy = update.isPending || createSubmit.isPending;
  const locked = busy || held || damaged || savedWarnings !== null;

  const value = parseRuleValue(valueDraft);
  const windowFailure = checkWindow(period.from, period.to, { allowOpen: true });
  const noteTooLong = note.length > NOTE_MAX;
  const targetValue = rule ? null : targetBody(target);
  const partyValid = rule !== null || party !== null;
  const valid = value.ok && windowFailure === null && !noteTooLong && partyValid && (rule !== null || targetValue !== null);

  const finish = (saved: CustomerPriceSaved) => {
    notify.success(t('customerPrices.saved'));
    if (saved.warnings.length > 0) setSavedWarnings(saved.warnings);
    else onClose();
  };

  const save = async () => {
    setShowErrors(true);
    if (!valid || !value.ok) return;
    setError(null);
    if (rule) {
      const body: UpdateCustomerPriceBody = {};
      const valueChanged = valueDraft.priceType !== rule.priceType || value.value !== normalizeCents4(rule.value);
      if (valueChanged) {
        body.priceType = valueDraft.priceType;
        body.value = value.value;
      }
      if (period.from !== (rule.validFrom ?? '')) body.validFrom = period.from === '' ? null : period.from;
      if (period.to !== (rule.validTo ?? '')) body.validTo = period.to === '' ? null : period.to;
      if (isActive !== rule.isActive) body.isActive = isActive;
      if (note.trim() !== (rule.note ?? '')) body.note = note.trim() === '' ? null : note.trim();
      if (Object.keys(body).length === 0) {
        onClose();
        return;
      }
      try {
        finish(await update.mutateAsync({ id: rule.id, body }));
      } catch (failure) {
        if (isSessionCancel(failure)) return;
        setError(failure);
      }
      return;
    }
    if (!party || !targetValue) return;
    const body: CreateCustomerPriceBody = {
      ...(party.kind === 'company' ? { companyId: party.id } : { personId: party.id }),
      ...targetValue,
      priceType: valueDraft.priceType,
      value: value.value,
      ...(period.from !== '' ? { validFrom: period.from } : {}),
      ...(period.to !== '' ? { validTo: period.to } : {}),
      isActive,
      ...(note.trim() !== '' ? { note: note.trim() } : {}),
    } as CreateCustomerPriceBody;
    const result = await createSubmit.submit(body);
    if (result.status === 'success') finish(result.result);
    else if (result.status === 'refused') setError(result.error);
  };

  const retry = async () => {
    // `canceled` shows nothing here: the screen of another owner is gone, and a refusal to retry is reported by the held request itself.
    const result = await createSubmit.retry();
    if (result.status === 'success') finish(result.result);
    else if (result.status === 'refused') setError(result.error);
  };

  const requestClose = () => {
    if (!busy && !held) onClose();
  };

  return (
    <ResponsiveDialog
      open
      onClose={requestClose}
      maxWidth="sm"
      fullWidth
      mobileTitle={rule ? t('customerPrices.editTitle') : t('customerPrices.addTitle')}
    >
      <DialogTitle>{rule ? t('customerPrices.editTitle') : t('customerPrices.addTitle')}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          {rule ? (
            <>
              <Typography variant="body1">
                <PartyName kind={rule.companyId ? 'company' : 'person'} id={(rule.companyId ?? rule.personId) as string} />
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {rule.partId ? <PartName id={rule.partId} /> : <CategoryName id={rule.categoryId} />}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {t('customerPrices.immutableNote')}
              </Typography>
            </>
          ) : (
            <>
              <PricingPartyPicker label={t('fields.customer')} value={party} onChange={setParty} disabled={locked} />
              {showErrors && !party && (
                <Typography variant="caption" color="error">
                  {t('validation.party.required')}
                </Typography>
              )}
              <TargetFields draft={target} onChange={setTarget} disabled={locked} showErrors={showErrors} />
            </>
          )}
          <RuleValueFields draft={valueDraft} onChange={setValueDraft} disabled={locked} showErrors={showErrors} />
          <WindowFields
            from={period.from}
            to={period.to}
            onChange={setPeriod}
            allowOpen
            fromLabel={t('fields.validFrom')}
            toLabel={t('fields.validTo')}
            disabled={locked}
            showErrors={showErrors}
          />
          <FormControlLabel
            control={<Switch checked={isActive} disabled={locked} onChange={(event) => setIsActive(event.target.checked)} />}
            label={t('fields.ruleActive')}
          />
          <TextField
            size="small"
            multiline
            minRows={2}
            label={t('fields.note')}
            value={note}
            disabled={locked}
            onChange={(event) => setNote(event.target.value)}
            error={noteTooLong}
            helperText={noteTooLong ? t('validation.note.tooLong', { max: NOTE_MAX }) : undefined}
          />
          {savedWarnings && <PricingWarnings warnings={savedWarnings} />}
          {createSubmit.held && (
            <UncertainOutcomeAlert
              what={t('customerPrices.uncertainWhat')}
              error={createSubmit.error}
              attempts={createSubmit.attempts}
              busy={createSubmit.isPending}
              onRetry={() => void retry()}
              onDiscard={createSubmit.discard}
              journalStatus={createSubmit.journalStatus}
            />
          )}
          <JournalStatusNotice status={createSubmit.journalStatus} holding={held} onDiscardDamaged={createSubmit.discardDamaged} />
          <PricingErrorAlert error={held ? null : error} />
        </Stack>
      </DialogContent>
      <DialogActions>
        {savedWarnings ? (
          <Button variant="contained" onClick={onClose}>
            {tc('close')}
          </Button>
        ) : (
          <>
            <Button onClick={requestClose} disabled={busy || held}>
              {tc('cancel')}
            </Button>
            <Button variant="contained" onClick={() => void save()} disabled={busy || held || damaged}>
              {busy ? tc('saving') : tc('save')}
            </Button>
          </>
        )}
      </DialogActions>
    </ResponsiveDialog>
  );
}

function RuleState({ rule }: { rule: CustomerPriceRule }) {
  const { t } = useTranslation('partPricing');
  if (!rule.isActive) return <Chip size="small" label={t('state.disabled')} />;
  return rule.activeToday ? (
    <Chip size="small" color="success" label={t('state.activeToday')} />
  ) : (
    <Chip size="small" color="warning" label={t('state.notActiveToday')} />
  );
}

function CustomerPricesPanelBody() {
  const { t, i18n } = useTranslation('partPricing');
  const { t: tc } = useTranslation('common');
  const notify = useNotify();
  const { canManage } = usePricingPermissions();
  const ruleValueText = useRuleValueText();
  const remove = useDeleteCustomerPrice();

  const [partyFilter, setPartyFilter] = useState<PickedParty | null>(null);
  const [targetFilter, setTargetFilter] = useState<TargetDraft>(emptyTarget);
  const [stateFilter, setStateFilter] = useState<'all' | 'enabled' | 'disabled'>('all');
  const targetFilterBody = targetBody(targetFilter);
  const filters: CustomerPriceFilters = {
    party: partyFilter ? { kind: partyFilter.kind, id: partyFilter.id } : undefined,
    target: targetFilterBody
      ? 'partId' in targetFilterBody
        ? { kind: 'part', id: targetFilterBody.partId }
        : { kind: 'category', id: targetFilterBody.categoryId }
      : undefined,
    isActive: stateFilter === 'all' ? undefined : stateFilter === 'enabled',
  };
  const prices = useCustomerPrices(filters);
  const rows = flattenPages(prices.data);
  const total = prices.data?.pages[prices.data.pages.length - 1]?.pagination.total ?? 0;

  const [dialog, setDialog] = useState<{ rule: CustomerPriceRule | null } | null>(null);
  const [deleting, setDeleting] = useState<CustomerPriceRule | null>(null);
  // A create whose outcome is unknown (also one found again after a reload) reopens its dialog: nothing else may start until the
  // user retries the same request or knowingly discards it.
  const { owner } = usePricingAccess();
  const unresolvedCreate = useJournalView(owner).entries.some((entry) => entry.slot === 'customer_price_create');
  const shownDialog = dialog ?? (unresolvedCreate && canManage ? { rule: null } : null);

  const confirmDelete = async () => {
    if (!deleting) return;
    const target = deleting;
    try {
      await remove.mutateAsync(target.id);
      notify.success(t('customerPrices.deleted'));
    } catch (failure) {
      if (isSessionCancel(failure)) return;
      notify.error(t('customerPrices.deleteFailed'), describePricingError(failure, t, tc));
    } finally {
      setDeleting((current) => (current?.id === target.id ? null : current));
    }
  };

  const windowText = (rule: CustomerPriceRule): string => {
    if (rule.validFrom && rule.validTo) return t('rule.window', { from: formatDate(rule.validFrom, i18n.language), to: formatDate(rule.validTo, i18n.language) });
    if (rule.validFrom) return t('rule.fromOnly', { from: formatDate(rule.validFrom, i18n.language) });
    if (rule.validTo) return t('rule.untilOnly', { to: formatDate(rule.validTo, i18n.language) });
    return t('rule.alwaysValid');
  };

  return (
    <Stack spacing={2}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
        <Box>
          <Typography variant="h6">{t('customerPrices.title')}</Typography>
          <Typography variant="body2" color="text.secondary">
            {t('customerPrices.intro')}
          </Typography>
        </Box>
        {canManage && (
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => setDialog({ rule: null })}>
            {t('customerPrices.add')}
          </Button>
        )}
      </Stack>
      <Alert severity="info">{t('hints.cheapestWins')}</Alert>

      <Accordion disableGutters variant="outlined" slotProps={{ transition: { unmountOnExit: true } }}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography>{t('list.filters')}</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Stack spacing={2}>
            <PricingPartyPicker label={t('fields.customer')} value={partyFilter} onChange={setPartyFilter} />
            <TargetFields draft={targetFilter} onChange={setTargetFilter} showErrors={false} />
            <TextField
              select
              size="small"
              label={t('fields.state')}
              value={stateFilter}
              onChange={(event) => setStateFilter(event.target.value as 'all' | 'enabled' | 'disabled')}
            >
              <MenuItem value="all">{tc('all')}</MenuItem>
              <MenuItem value="enabled">{t('state.enabledFilter')}</MenuItem>
              <MenuItem value="disabled">{t('state.disabled')}</MenuItem>
            </TextField>
          </Stack>
        </AccordionDetails>
      </Accordion>

      {prices.isError && <PricingErrorAlert error={prices.error} />}
      {prices.isLoading && <Typography color="text.secondary">{tc('loading')}</Typography>}
      {!prices.isLoading && !prices.isError && rows.length === 0 && <Typography color="text.secondary">{t('customerPrices.empty')}</Typography>}
      {rows.length > 0 && (
        <TableContainer>
          <Table size="small" aria-label={t('customerPrices.title')}>
            <TableHead>
              <TableRow>
                <TableCell>{t('fields.customer')}</TableCell>
                <TableCell>{t('fields.target')}</TableCell>
                <TableCell>{t('fields.price')}</TableCell>
                <TableCell>{t('fields.validity')}</TableCell>
                <TableCell>{t('fields.state')}</TableCell>
                {canManage && <TableCell align="right">{tc('actions')}</TableCell>}
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((row) => {
                const kind = row.companyId ? 'company' : 'person';
                return (
                  <TableRow key={row.id} hover>
                    <TableCell>
                      <PartyName kind={kind} id={(row.companyId ?? row.personId) as string} />
                      <Typography variant="caption" color="text.secondary" display="block">
                        {t(`partyKind.${kind}`)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      {row.partId ? <PartName id={row.partId} /> : <CategoryName id={row.categoryId} />}
                      <Typography variant="caption" color="text.secondary" display="block">
                        {t(row.partId ? 'targetKind.part' : 'targetKind.category')}
                      </Typography>
                    </TableCell>
                    <TableCell>{ruleValueText(row.priceType, row.value)}</TableCell>
                    <TableCell>{windowText(row)}</TableCell>
                    <TableCell>
                      <RuleState rule={row} />
                    </TableCell>
                    {canManage && (
                      <TableCell align="right">
                        <Tooltip title={tc('edit')}>
                          <IconButton size="small" aria-label={tc('edit')} onClick={() => setDialog({ rule: row })}>
                            <EditOutlinedIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title={tc('delete')}>
                          <IconButton size="small" aria-label={tc('delete')} onClick={() => setDeleting(row)}>
                            <DeleteOutlineIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    )}
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}
      {rows.length > 0 && (
        <Stack direction="row" alignItems="center" spacing={2}>
          <Typography variant="caption" color="text.secondary">
            {t('list.showing', { shown: rows.length, total })}
          </Typography>
          {prices.hasNextPage && (
            <Button size="small" onClick={() => void prices.fetchNextPage()} disabled={prices.isFetchingNextPage}>
              {prices.isFetchingNextPage ? tc('loading') : tc('loadMore')}
            </Button>
          )}
        </Stack>
      )}

      {shownDialog && <CustomerPriceDialog key={shownDialog.rule?.id ?? 'new'} rule={shownDialog.rule} onClose={() => setDialog(null)} />}
      <ConfirmDialog
        open={deleting !== null}
        title={t('customerPrices.deleteTitle')}
        message={t('customerPrices.deleteMessage')}
        confirmText={tc('delete')}
        confirmColor="error"
        loading={remove.isPending}
        onConfirm={() => void confirmDelete()}
        onCancel={() => {
          if (!remove.isPending) setDeleting(null);
        }}
      />
    </Stack>
  );
}

export function CustomerPricesPanel() {
  return (
    <OwnedBoundary>
      <CustomerPricesPanelBody />
    </OwnedBoundary>
  );
}
